import * as Sentry from '@sentry/react'

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined

/**
 * Check whether any exception in the event originated from Monaco Editor
 * by inspecting stack frames for known Monaco CDN / bundle paths.
 */
function hasMonacoFrames(event: Sentry.ErrorEvent): boolean {
  for (const value of event.exception?.values ?? []) {
    for (const frame of value.stacktrace?.frames ?? []) {
      const filename = frame.filename ?? ''
      if (
        filename.includes('monaco-editor') ||
        filename.includes('vs/editor')
      ) {
        return true
      }
    }
  }
  return false
}

/**
 * Extract the primary error message from a Sentry event, preferring the
 * original exception (which Sentry may wrap) over the processed event value.
 */
function extractMessage(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): string {
  if (hint.originalException instanceof Error) {
    return hint.originalException.message
  }
  if (typeof hint.originalException === 'string') {
    return hint.originalException
  }
  return event.exception?.values?.[0]?.value ?? event.message ?? ''
}

/**
 * Returns true if the event is a known Monaco Editor internal error that has
 * no user-facing impact and should be dropped before reaching Sentry.
 */
function isMonacoNoise(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): boolean {
  if (!hasMonacoFrames(event)) return false

  const message = extractMessage(event, hint)

  // Monaco's document.caretPositionFromPoint() returns null (mouse outside
  // viewport, rapid tab switches, Shadow DOM boundaries) but Monaco doesn't
  // null-check before accessing .offsetNode.
  // See: https://github.com/microsoft/monaco-editor/issues/4548
  if (message.includes('offsetNode') && message.includes('is null')) {
    return true
  }

  // Monaco uses a cancellation token pattern internally. When async operations
  // (autocomplete, hover, validation) are in-flight and the component unmounts
  // or the user navigates away, Monaco throws a CancellationError("Canceled").
  // This is expected behavior — not a bug.
  if (message === 'Canceled') {
    return true
  }

  return false
}

if (dsn) {
  Sentry.init({
    dsn,
    environment:
      import.meta.env.VITE_VERCEL_ENV || import.meta.env.MODE || 'development',

    // Disable performance monitoring — we use OpenTelemetry for tracing
    tracesSampleRate: 0,

    beforeSend(event, hint) {
      if (isMonacoNoise(event, hint)) return null
      return event
    },
  })
}

export { Sentry }

/** @internal Exported for testing only */
export { isMonacoNoise as _isMonacoNoise }
